import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DailySum } from './DailySum';
import { useNavigate } from 'react-router-dom';
import { QUICK_DEPOSIT_OPTIONS } from './quick-entry/quickEntryCore.mjs';

function deriveTotalsFromEntries(entries = []) {
  const now = new Date();
  let daily = 0;
  let total = 0;

  for (const entry of entries) {
    const amount = Number(entry?.amount || 0);
    if (!Number.isFinite(amount)) continue;
    total += amount;

    if (!entry?.timestamp) continue;
    const dt = new Date(entry.timestamp);
    if (
      dt.getFullYear() === now.getFullYear() &&
      dt.getMonth() === now.getMonth() &&
      dt.getDate() === now.getDate()
    ) {
      daily += amount;
    }
  }

  return {
    total_collected: total,
    daily_collected: daily,
    available: total
  };
}

export function Overview({ add, giveXP, user, freeValue, setFreeValue, setXpUp, entries }) {
  const navigate = useNavigate();
  const derivedTotals = useMemo(() => deriveTotalsFromEntries(entries), [entries]);
  const [totals, setTotals] = useState(derivedTotals);
  const [pendingQuickAdd, setPendingQuickAdd] = useState(false);
  const [syncHint, setSyncHint] = useState('');
  const refreshTimerRef = useRef(null);
  const nonce = window?.pfandpiratData?.nonce ?? '';

  async function refreshTotals() {
    const resp = await fetch('/wp-json/pfandpirat/v1/totals', {
      credentials: 'include',
      headers: { 'X-WP-Nonce': nonce }
    });
    const data = await resp.json();
    setTotals(data);
    setSyncHint('');
    return data;
  }

  function scheduleRefreshTotals() {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = setTimeout(() => {
      refreshTotals().catch(() => {
        setSyncHint('');
      });
      refreshTimerRef.current = null;
    }, 1200);
  }

  useEffect(() => {
    refreshTotals().catch(() => {
      setTotals(derivedTotals);
    });

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!totals) {
      setTotals(derivedTotals);
    }
  }, [derivedTotals, totals]);

  function applyOptimisticTotals(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return;

    setTotals(prev => {
      const base = prev || derivedTotals;
      return {
        ...base,
        total_collected: Number(base.total_collected || 0) + n,
        daily_collected: Number(base.daily_collected || 0) + n,
        available: Number(base.available || 0) + n,
      };
    });

    setSyncHint('wird synchronisiert …');
  }

  async function handleAdd(value) {
    if (pendingQuickAdd) return;
    setPendingQuickAdd(true);

    applyOptimisticTotals(value);

    try {
      await Promise.resolve(add(value, { activity_source: 'quick_add' }, user?.id));

      if (setXpUp) {
        setXpUp(true);
        setTimeout(() => setXpUp(false), 700);
      }

      scheduleRefreshTotals();
    } finally {
      // absichtlich schnell wieder freigeben
      setTimeout(() => setPendingQuickAdd(false), 120);
    }
  }

  async function handleFreeAdd() {
    const v = parseFloat(String(freeValue ?? '').replace(',', '.'));
    if (isNaN(v) || pendingQuickAdd) return;

    setPendingQuickAdd(true);
    applyOptimisticTotals(v);

    try {
      await Promise.resolve(add(v, { activity_source: 'freitext' }, user?.id));
      setFreeValue?.('');

      if (setXpUp) {
        setXpUp(true);
        setTimeout(() => setXpUp(false), 700);
      }

      scheduleRefreshTotals();
    } finally {
      setTimeout(() => setPendingQuickAdd(false), 120);
    }
  }

  return (
    <section className="pfp-overview-cashbox" aria-label="Pfand schnell erfassen">
      <DailySum
        entries={[]}
        totalOverride={Number(totals?.total_collected ?? derivedTotals.total_collected)}
        dailyOverride={Number(totals?.daily_collected ?? derivedTotals.daily_collected)}
      />

      {syncHint ? (
        <div className="pfp-overview-sync-hint">
          {syncHint}
        </div>
      ) : null}

      <div className="pfp-overview-quick-values">
        {QUICK_DEPOSIT_OPTIONS.map((option) => (
          <button key={option.id} className="pfand-button" disabled={pendingQuickAdd} onClick={() => handleAdd(option.value)}>
            +{option.value.toFixed(2).replace('.', ',')} €
          </button>
        ))}
      </div>

      <div className="pfp-overview-free-add">
        <input
          type="text"
          placeholder="0,15"
          value={freeValue}
          onChange={e => setFreeValue?.(e.target.value)}
        />
        <button className="pfand-button" disabled={pendingQuickAdd} onClick={handleFreeAdd}>Hinzufügen</button>
      </div>

      <div className="pfp-overview-search-action">
        <button
          onClick={() => navigate('/search')}
          className="pfp-overview-search-button"
        >
          🔍 Getränkesuche starten
        </button>
      </div>
    </section>
  );
}
